import { useState, useCallback, useEffect } from 'react'
import settings from 'popups/Settings'
import { FilterType } from '../../../modules/TodoFilter'

interface TodoLocalSettings {
  collapsedSectionKeys?: Record<string, string[]>
  [key: string]: unknown
}

interface UseCollapsedSectionsReturn {
  collapsedSectionKeys: string[]
  setCollapsedSectionKeys: (keys: string[] | ((prev: string[]) => string[])) => void
}

/**
 * 섹션 접기/펼치기 상태를 관리하는 hook
 * @param currentFilter - 현재 필터
 * @returns { collapsedSectionKeys, setCollapsedSectionKeys }
 */
export const useCollapsedSections = (currentFilter: FilterType): UseCollapsedSectionsReturn => {
  // Get initial collapsed section keys from settings
  const getInitialCollapsedKeys = (): string[] => {
    const todoLocalSettings: TodoLocalSettings = settings.getValue('localSettings').todo || {}
    const savedCollapsedKeys = todoLocalSettings.collapsedSectionKeys || {}
    return savedCollapsedKeys[currentFilter] || []
  }

  const [collapsedSectionKeys, setCollapsedSectionKeysState] =
    useState<string[]>(getInitialCollapsedKeys)

  // Custom setter that saves to settings
  const setCollapsedSectionKeys = useCallback(
    (newKeys: string[] | ((prev: string[]) => string[])) => {
      const keysToSave = typeof newKeys === 'function' ? newKeys(collapsedSectionKeys) : newKeys
      setCollapsedSectionKeysState(keysToSave)

      // collapsedSectionKeys 변경시 마다, settings 에 저장한다.
      const localSettings = settings.getValue('localSettings')
      const todoLocalSettings: TodoLocalSettings = localSettings.todo || {}
      const currentCollapsedKeys = todoLocalSettings.collapsedSectionKeys || {}
      const updatedCollapsedKeys = {
        ...currentCollapsedKeys,
        [currentFilter]: keysToSave, // 현재 필터의 설정만 업데이트
      }

      settings.setValue('localSettings', {
        ...localSettings,
        todo: {
          ...todoLocalSettings,
          collapsedSectionKeys: updatedCollapsedKeys,
        },
      })
    },
    [currentFilter, collapsedSectionKeys],
  )

  // Update collapsed section keys when filter changes - load from settings
  useEffect(() => {
    const todoLocalSettings: TodoLocalSettings = settings.getValue('localSettings').todo || {}
    const savedCollapsedKeys = todoLocalSettings.collapsedSectionKeys || {}
    const filterCollapsedKeys = savedCollapsedKeys[currentFilter] || []

    setCollapsedSectionKeysState(filterCollapsedKeys)
  }, [currentFilter])

  return {
    collapsedSectionKeys,
    setCollapsedSectionKeys,
  }
}
